import { createSlice } from '@reduxjs/toolkit';
import { createUniqueIdAction } from "../actions/CreateUniqueIdAction";
import dayjs from "dayjs";
import { ReservationType } from 'src/utils/common';
import { providerHelpers } from 'src/utils/providerConfig';

const initialState = {
    reservationRequest: {
        name: '',
        hotels: [],
        services: [],
        transfers: [],
        provider: null,
    },
    reservationRequestSummary: {
        tourStartDate: '', //minimum check-in date
        tourEndDate: '', //maximum check-out date
        totalAdultCount: 0,
        totalChildCount: 0,
        totalAmount: 0, // total ClientSellTotal
        currencyCode: '',
        propertyCount: 0, // total number of unique properties (hotels)
        serviceCount: 0, // total number of unique services
        totalNights: 0, // total nights across all hotels
    },
    reservationCartItinerary: [],
};

const generateItinerary = (state) => {
    const hotels = JSON.parse(JSON.stringify(state.reservationRequest.hotels || []));
    const services = JSON.parse(JSON.stringify(state.reservationRequest.services || [])).filter(service => Array.isArray(service.Units) && service.Units.length > 0); // Exclude services with no units

    const allItems = [...hotels, ...services].sort((a, b) =>
        new Date(a.CheckIn) - new Date(b.CheckIn)
    );

    const daysMap = new Map();

    allItems.forEach(item => {
        const dateKey = item.CheckIn;
        if (!daysMap.has(dateKey)) {
            daysMap.set(dateKey, {
                date: dateKey,
                items: []
            });
        }

        const entry = {
            id: item.Id,
            entityId: item.PropertyId || item.ServiceId,
            name: item.Name,
            type: item.PropertyId ? ReservationType.hotel : ReservationType.service,
            checkIn: item.CheckIn,
            checkOut: item.CheckOut,
            totalCost: item.ClientSellTotal,
            details: item.Rooms ? {
                rooms: item.Rooms.map(room => ({
                    name: room.Name,
                    adults: room.Adults,
                    children: room.Children?.length || 0,
                    cost: room.TotalCost
                }))
            } : {
                units: item.Units.map(unit => ({
                    name: unit.Name,
                    adults: unit.Adults,
                    children: unit.Children?.length || 0,
                    cost: item.ClientSellTotal
                }))
            }
        };

        daysMap.get(dateKey).items.push(entry);
    });

    state.reservationCartItinerary = Array.from(daysMap.values());
};

const updateSummary = (state) => {
    const { hotels, services } = state.reservationRequest;

    // Get all dates
    const allDates = [
        ...hotels.map(h => h.CheckIn),
        ...hotels.map(h => h.CheckOut),
        ...services.map(s => s.CheckIn),
        ...services.map(s => s.CheckOut)
    ].filter(date => date);

    const dateOccupancy = {};

    hotels.forEach(hotel => {
        const startDate = new Date(hotel.CheckIn);
        const endDate = new Date(hotel.CheckOut);

        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
            const dateKey = d.toISOString().split('T')[0];

            if (!dateOccupancy[dateKey]) {
                dateOccupancy[dateKey] = { adults: 0, children: 0 };
            }

            let hotelAdults = 0;
            let hotelChildren = 0;

            hotel.Rooms?.forEach(room => {
                hotelAdults += room.Adults || 0;
                hotelChildren += room.Children?.length || 0;
            });

            dateOccupancy[dateKey].adults = Math.max(dateOccupancy[dateKey].adults, hotelAdults);
            dateOccupancy[dateKey].children = Math.max(dateOccupancy[dateKey].children, hotelChildren);
        }
    });

    services.forEach(service => {
        const startDate = new Date(service.CheckIn);
        const endDate = new Date(service.CheckOut);

        for (let d = new Date(startDate); d <= endDate; d.setDate(d.getDate() + 1)) {
            const dateKey = d.toISOString().split('T')[0];

            if (!dateOccupancy[dateKey]) {
                dateOccupancy[dateKey] = { adults: 0, children: 0 };
            }

            let serviceAdults = 0;
            let serviceChildren = 0;

            service.Units?.forEach(unit => {
                serviceAdults += unit.Adults || 0;
                serviceChildren += unit.Children?.length || 0;
            });

            dateOccupancy[dateKey].adults = Math.max(dateOccupancy[dateKey].adults, serviceAdults);
            dateOccupancy[dateKey].children = Math.max(dateOccupancy[dateKey].children, serviceChildren);
        }
    });

    let maxAdults = 0;
    let maxChildren = 0;

    Object.values(dateOccupancy).forEach(day => {
        maxAdults = Math.max(maxAdults, day.adults);
        maxChildren = Math.max(maxChildren, day.children);
    });

    const transferCost = state.reservationRequest.transfers?.[0]?.cost ?? 0;

    const totalAmount = [
        ...hotels.map(h => h.ClientSellTotal || 0),
        ...services.map(s => s.ClientSellTotal || 0),
        transferCost,
    ].reduce((sum, amount) => sum + amount, 0);

    const minDate = allDates.length > 0 ? Math.min(...allDates.map(date => new Date(date).getTime())) : null;
    const maxDate = allDates.length > 0 ? Math.max(...allDates.map(date => new Date(date).getTime())) : null;

    let totalNights = minDate && maxDate
        ? Math.max(0, dayjs(maxDate).diff(dayjs(minDate), 'day'))
        : 0;

    state.reservationRequestSummary = {
        tourStartDate: minDate ? new Date(minDate).toISOString().split('T')[0] : '',
        tourEndDate: maxDate ? new Date(maxDate).toISOString().split('T')[0] : '',
        totalAdultCount: maxAdults,
        totalChildCount: maxChildren,
        totalAmount: totalAmount,
        currencyCode: hotels[0]?.CurrencyCode || services[0]?.CurrencyCode || '',
        propertyCount: new Set(hotels.map(h => h.PropertyId)).size,
        serviceCount: new Set(services.map(s => s.ServiceId)).size,
        totalNights: totalNights
    };
};

const removeHotelsWithNoRooms = (state) => {
    state.reservationRequest.hotels = state.reservationRequest.hotels.filter(hotel =>
        hotel.Rooms && hotel.Rooms.length > 0
    );
};

const reservationRequestItemsSlice = createSlice({
    name: 'reservationRequestItems',
    initialState,
    reducers: {
        setName(state, action) {
            state.reservationRequest.name = action.payload;
            updateSummary(state);
        },

        addOrUpdateItem(state, action) {
            const { type, item, provider } = action.payload;

            if (type === ReservationType.hotel) {
                const currentProvider = state.reservationRequest.provider;
                const newProvider = provider;

                if (currentProvider && currentProvider !== newProvider) {
                    return;
                }

                if (currentProvider && currentProvider === newProvider) {
                    const hasHotels = state.reservationRequest.hotels.length > 0;
                    const hasServices = state.reservationRequest.services.length > 0;

                    if (
                        (type === ReservationType.hotel && hasServices) ||
                        (type === ReservationType.service && hasHotels)
                    ) {
                        if (!providerHelpers.allowsMixedItems(currentProvider)) {
                            return;
                        }
                    }
                }

                // Set provider only if cart is empty
                if (!currentProvider && newProvider) {
                    state.reservationRequest.provider = newProvider;
                }

                const itemWithId = { ...item, Id: item.Id || createUniqueIdAction() };

                const existingIndex = state.reservationRequest.hotels.findIndex(
                    hotel => hotel.PropertyId === item.PropertyId &&
                        hotel.CheckIn === item.CheckIn &&
                        hotel.CheckOut === item.CheckOut
                );

                if (existingIndex >= 0) {
                    // Update existing hotel
                    state.reservationRequest.hotels[existingIndex] = {
                        ...itemWithId,
                        Id: state.reservationRequest.hotels[existingIndex].Id || createUniqueIdAction()
                    };
                } else {
                    // Add new hotel
                    if (providerHelpers.shouldShowOnlySingleHotel(newProvider)) {
                        if (item.Rooms?.length > 0) {
                            state.reservationRequest.hotels = [itemWithId];
                        }
                    } else {
                        if (item.Rooms?.length > 0) {
                            state.reservationRequest.hotels.push(itemWithId);
                        }
                    }
                }
            }
            else if (type === ReservationType.service) {
                const currentProvider = state.reservationRequest.provider;
                const newProvider = provider;

                // Set provider only if cart is empty
                if (!currentProvider && newProvider) {
                    state.reservationRequest.provider = newProvider;
                }

                const itemWithId = { ...item, Id: item.Id || createUniqueIdAction() };

                const existingIndex = state.reservationRequest.services.findIndex(
                    service => service.ServiceId === item.ServiceId &&
                        service.CheckIn === item.CheckIn &&
                        service.CheckOut === item.CheckOut
                );

                if (existingIndex >= 0) {
                    // Update existing service
                    state.reservationRequest.services[existingIndex] = {
                        ...item,
                        Units: Array.isArray(item.Units) && item.Units.length > 0
                            ? [...item.Units] // Use new Units with a copy
                            : Array.isArray(state.reservationRequest.services[existingIndex].Units)
                                ? [...state.reservationRequest.services[existingIndex].Units] // Preserve existing Units with a "State" copy
                                : [],
                        Id: state.reservationRequest.services[existingIndex].Id || createUniqueIdAction()
                    };
                } else {
                    // Add new service
                    state.reservationRequest.services.push(itemWithId); // Allow empty Units array
                }
            }
            updateSummary(state);
            generateItinerary(state);
            removeHotelsWithNoRooms(state);
        },

        removeReservedItem(state, action) {
            const { type, id, unitIndex } = action.payload;

            if (type === ReservationType.hotel) {
                state.reservationRequest.hotels = state.reservationRequest.hotels.filter(item => item.Id !== id);
            } else if (type === ReservationType.service) {
                if (unitIndex !== undefined && unitIndex !== null) {
                    const service = state.reservationRequest.services.find(item => item.Id === id);
                    if (service && Array.isArray(service.Units)) {
                        service.Units = service.Units.filter((_, index) => index !== unitIndex);
                        if (service.Units.length === 0) {
                            state.reservationRequest.services = state.reservationRequest.services.filter(item => item.Id !== id);
                        }
                    }
                } else {
                    state.reservationRequest.services = state.reservationRequest.services.filter(item => item.Id !== id);
                }
            }

            // clear provider if BOTH hotels AND services are empty
            const hasHotels = state.reservationRequest.hotels.length > 0;
            const hasServices = state.reservationRequest.services.length > 0;

            if (!hasHotels && !hasServices) {
                state.reservationRequest.provider = null;
            }

            updateSummary(state);
            generateItinerary(state);
            removeHotelsWithNoRooms(state);
        },

        removeHotelsOrService(state, action) {
            const { type } = action.payload;

            if (type === ReservationType.hotel) {
                state.reservationRequest.hotels = [];
            } else if (type === ReservationType.service) {
                state.reservationRequest.services = [];
            }

            // clear provider if BOTH hotels AND services are empty
            const hasHotels = state.reservationRequest.hotels.length > 0;
            const hasServices = state.reservationRequest.services.length > 0;

            if (!hasHotels && !hasServices) {
                state.reservationRequest.provider = null;
            }

            updateSummary(state);
            generateItinerary(state);
        },

        addTransferUnit: (state, action) => {
            const unit = action.payload;

            const previousCost = state.reservationRequest.transfers?.[0]?.cost ?? 0;

            state.reservationRequest.transfers = [unit];

            state.reservationRequestSummary.totalAmount =
                (state.reservationRequestSummary.totalAmount ?? 0) - previousCost + (unit.cost ?? 0);
        },

        removeTransferUnit: (state) => {
            const existing = state.reservationRequest.transfers?.[0];
            if (existing) {
                state.reservationRequestSummary.totalAmount =
                    Math.max(0, (state.reservationRequestSummary.totalAmount ?? 0) - (existing.cost ?? 0));
            }
            state.reservationRequest.transfers = [];
        },

        clearItems(state) {
            state.reservationRequest = {
                name: '',
                hotels: [],
                services: [],
                transfers: [],
                provider: null,
            };
            updateSummary(state);
            state.reservationRequestSummary = {
                tourStartDate: '',
                tourEndDate: '',
                totalAdultCount: 0,
                totalChildCount: 0,
                totalAmount: 0,
                currencyCode: '',
                propertyCount: 0,
                serviceCount: 0,
                totalNights: 0,
            };
            state.reservationCartItinerary = [];
        },
    },
});

export const {
    setName,
    addOrUpdateItem,
    removeReservedItem,
    removeHotelsOrService,
    clearItems,
    addTransferUnit,
    removeTransferUnit,
} = reservationRequestItemsSlice.actions;

export default reservationRequestItemsSlice.reducer;